/**
 * GlydeVoice SDK - Voice Agent Client for GLYDE Unity
 *
 * Provides voice interaction capabilities with GLYDE AI agents through the Deepgram
 * Voice Agent API. Built on the official `@deepgram/agents` headless browser SDK:
 * `AgentSession` (WebSocket session), `AgentMicrophone` (capture), and `AgentPlayer`
 * (playback) replace the previous custom WebSocket + AudioWorklet implementation.
 * Supports multiple authentication methods: publishableKey, apiKey, and JWT token.
 *
 * Context Types:
 * - screening: Known candidate with application_uuid - screening interview
 * - recruiter: Internal recruiter copilot
 * - custom: Custom context with overridden prompts
 * - phone: Phone agent context
 * - discovery: Unknown candidate, no job - guide to opportunities (publishable_key only)
 * - job_apply: Unknown candidate, known job - apply to specific job (contextId = job_uuid)
 * - candidate_discover: Known candidate (OTP verified), no job - explore opportunities (contextId = candidate_uuid)
 * - candidate_apply: Known candidate, known job - apply and start screening (contextId = candidate_uuid:job_uuid)
 *
 * @example
 * // Screening interview for existing application
 * const voice = new GlydeVoice({
 *   publishableKey: 'pk_...',
 *   contextType: 'screening',
 *   contextId: 'application-uuid'
 * });
 *
 * @example
 * // Discovery mode - unknown candidate exploring opportunities
 * const voice = new GlydeVoice({
 *   publishableKey: 'pk_...',
 *   contextType: 'discovery'
 * });
 *
 * @example
 * // Job apply - unknown candidate applying to specific job
 * const voice = new GlydeVoice({
 *   publishableKey: 'pk_...',
 *   contextType: 'job_apply',
 *   contextId: 'job-uuid'
 * });
 *
 * @example
 * // Using JWT token (GLYDEBuddy Teams app)
 * const voice = new GlydeVoice({
 *   authToken: userSession.accessToken,
 *   contextType: 'recruiter'
 * });
 */

import {
  AgentSession,
  AgentMicrophone,
  AgentPlayer,
  type AgentSettingsObject,
  type FunctionCallRequestMessage,
} from '@deepgram/agents';

import type {
  VoiceEvent,
  VoiceContextType,
  GlydeVoiceConfig,
  DeepgramAgentConfig,
  VoiceConfig,
  VoiceLanguageConfig,
  LanguageChangedPayload
} from './core/types';
import { BaseGlydeClient } from './core/BaseGlydeClient';
import { ContextSwitchManager } from './core/ContextSwitchManager';

// Re-export types for consumers who import from GlydeVoice
export type { VoiceContextType, GlydeVoiceConfig, VoiceConfig, DeepgramAgentConfig, VoiceLanguageConfig };

/**
 * GlydeVoice - Voice Agent Client
 *
 * Connects to Deepgram Voice Agent API for bidirectional voice conversation
 * via the official `@deepgram/agents` core SDK:
 * - `AgentSession`: WebSocket session (Settings, KeepAlive, typed events)
 * - `AgentMicrophone`: AudioWorklet microphone capture (linear16 PCM)
 * - `AgentPlayer`: PCM playback with instant interruption via `interrupt()`
 *
 * The Unity backend remains the control plane: it issues the ephemeral
 * Deepgram token, provides the (server-controlled) system prompt, greeting,
 * and function definitions, and executes all function calls through
 * `/api/unity/voice/function`. The public API of this class is unchanged from
 * the pre-`@deepgram/agents` implementation (v1.7.x) — consumers such as
 * GLYDE Screen's `useGlydeVoice` composable require no changes.
 */
export class GlydeVoice extends BaseGlydeClient<GlydeVoiceConfig, VoiceEvent> {
  private active = false;
  private serverConfig: VoiceConfig | null = null;

  // Deepgram core SDK primitives (replace the previous hand-rolled
  // WebSocket + AudioWorklet implementation)
  private session: AgentSession | null = null;
  private microphone: AgentMicrophone | null = null;
  private player: AgentPlayer | null = null;

  /** Gate: microphone frames are only forwarded once settings are applied */
  private micLive = false;
  private isMuted = false;

  // Audio settings
  private readonly outputSampleRate = 24000;  // Deepgram TTS output rate
  /**
   * Microphone capture rate. Resolved to the device's native AudioContext
   * rate in start(): forcing a rate that differs from the hardware (e.g.
   * 48000 on a 44.1 kHz Mac input) makes MediaStreamAudioSourceNode capture
   * silence (all-zero PCM) on some browser/device combinations.
   */
  private inputSampleRate = 48000;
  private readonly playbackSpeed: number;     // Native Deepgram TTS speed (0.7-1.5, 1.0 = normal)

  /** Consecutive all-zero mic frames seen; used to warn once about silent capture */
  private silentFrameCount = 0;
  private silenceWarned = false;

  // Agent state
  private isAgentSpeaking = false;

  /** Poll timer that detects when queued agent audio has finished playing */
  private playbackDrainTimer: ReturnType<typeof setInterval> | null = null;

  // Context switch manager (replaces inline pending reinit logic)
  private contextSwitchManager: ContextSwitchManager;

  // Conversation history
  private conversationHistory: Array<{ role: 'user' | 'agent'; content: string }> = [];

  // Session context for function calls (passed to backend for data controls and auth body when known)
  private sessionContext: {
    clientUuid?: string;
    contextId?: string;
    contextType?: VoiceContextType;
    currentJobUuid?: string;
    candidateUuid?: string;
  } = {};

  // ---- Language / voice switching state ----
  /** Resolved voice map + switching policy from the server (config or auth response). */
  private languageConfig: VoiceLanguageConfig | null = null;
  /** Language whose voice is currently active on the Deepgram session. */
  private activeLanguage: string | null = null;
  /** Whether automatic switching on detected language is permitted this session. */
  private autoSwitchEnabled = false;

  /**
   * Create a new GlydeVoice instance
   * @param config - Configuration options
   */
  constructor(config: GlydeVoiceConfig) {
    super(config);
    console.log('[GlydeVoice] Initialized with Unity API URL:', this.unityUrl);

    // Clamp to Deepgram's supported native speed range (0.7 - 1.5).
    this.playbackSpeed = Math.max(0.7, Math.min(1.5, config.playbackSpeed ?? 1.2));

    if (config.conversationHistory) {
      this.conversationHistory = config.conversationHistory;
    }

    this.contextSwitchManager = new ContextSwitchManager({
      fallbackTimeoutMs: 30000,
      onContextSwitchRequested: (payload) => {
        this.emitEvent({ type: 'context_switch', payload });
      }
    });
    this.contextSwitchManager.setCallbacks({
      performSmoothUpdate: (ct, ci) => this.updateContextAndPrompt(ct, ci),
      performFullReinit: (ct, ci) => this.reinitializeWithContext(ct, ci),
      canSmoothUpdate: () => this.session?.state === 'connected'
    });
  }

  /**
   * Fetch voice configuration from Unity API for the current config context.
   * @returns Voice configuration including system prompt, tools, and Deepgram settings
   */
  private async fetchConfig(): Promise<VoiceConfig> {
    return this.fetchConfigForContext(this.config.contextType, this.config.contextId);
  }

  /**
   * Fetch voice configuration from Unity API for a given context (e.g. for context switch).
   * @param contextType - Voice context type
   * @param contextId - Optional context ID (required for screening; candidate_uuid for candidate_discover)
   * @returns Voice configuration including system prompt and Deepgram settings
   */
  private async fetchConfigForContext(contextType: string, contextId?: string): Promise<VoiceConfig> {
    const configUrl = `${this.unityUrl}/api/unity/voice/config/${contextType}`;
    let configPath = contextId ? `${configUrl}/${encodeURIComponent(contextId)}` : configUrl;
    const params = new URLSearchParams();
    if (this.config.skipContinuityLimit === true) {
      params.set('skip_continuity_limit', 'true');
    }
    if (this.config.limitToLast != null && this.config.limitToLast > 0) {
      params.set('limit_to_last', String(this.config.limitToLast));
    }
    if (params.toString()) {
      configPath += (configPath.includes('?') ? '&' : '?') + params.toString();
    }

    const response = await fetch(configPath, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error?.message || errBody.message || `Failed to fetch voice config: ${response.status}`);
    }

    const { data } = await response.json();
    return data as VoiceConfig;
  }

  /**
   * Authenticate the voice session with Unity and retrieve the ephemeral
   * Deepgram token plus server-controlled agent configuration.
   * @returns Auth payload: token, agent_config, and optional deepgram_config
   */
  private async authenticateSession(): Promise<{
    token: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    agent_config?: any;
    deepgram_config?: DeepgramAgentConfig;
    language_config?: VoiceLanguageConfig;
  }> {
    const authBody: Record<string, unknown> = {
      context_id: this.config.contextId,
      context_type: this.config.contextType,
      domain: typeof window !== 'undefined' ? window.location.hostname : 'localhost'
    };
    if (this.sessionContext?.clientUuid) {
      authBody.client_uuid = this.sessionContext.clientUuid;
    }

    if (this.config.contextType === 'screening' && this.config.contextId) {
      authBody.application_uuid = this.config.contextId;
    }

    if (this.config.contextType === 'candidate_discover' && this.config.contextId) {
      authBody.candidate_uuid = this.config.contextId;
    } else if (this.sessionContext?.candidateUuid) {
      authBody.candidate_uuid = this.sessionContext.candidateUuid;
    }
    if (this.sessionContext?.currentJobUuid) {
      authBody.job_uuid = this.sessionContext.currentJobUuid;
    }

    if (this.config.deepgramConfig) {
      authBody.deepgram_config = this.config.deepgramConfig;
    }
    if (this.conversationHistory.length > 0) {
      authBody.conversation_history = this.conversationHistory;
    }
    if (this.config.skipContinuityLimit === true) {
      authBody.skip_continuity_limit = true;
    }
    if (this.config.limitToLast != null && this.config.limitToLast > 0) {
      authBody.limit_to_last = this.config.limitToLast;
    }

    const authResponse = await fetch(`${this.unityUrl}/api/unity/voice/auth`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(authBody)
    });

    if (!authResponse.ok) {
      const error = await authResponse.json();
      throw new Error(error.error?.message || error.message || 'Failed to authenticate voice session');
    }

    const { data } = await authResponse.json();
    return {
      token: data.token,
      agent_config: data.agent_config,
      deepgram_config: data.deepgram_config,
      language_config: data.language_config
    };
  }

  /**
   * Build the Deepgram agent settings object from the resolved server config.
   * Server-defined functions plus optional client `additionalFunctions` are merged;
   * system prompt, greeting, and native TTS speed are applied here.
   *
   * @param deepgramConfig - Resolved Deepgram config (client override > auth response > server config)
   * @param systemPrompt - Server-controlled system prompt (sent as `think.prompt`)
   * @param greeting - Server-controlled greeting (omitted when not provided)
   * @returns Agent settings object for AgentSession
   */
  private buildAgentSettings(
    deepgramConfig: DeepgramAgentConfig | undefined,
    systemPrompt: string,
    greeting: string | undefined
  ): AgentSettingsObject {
    // Only provide minimal fallback for audio settings if server config is missing
    // Mirrors the Unity platform default (bilingual EN/ES). The initial speak
    // voice is English-native (thalia); Spanish turns swap to the Spanish
    // voice via UpdateSpeak once the server exposes the language map.
    const finalDeepgramConfig = deepgramConfig || {
      think: { provider: { type: 'open_ai', model: 'gpt-4.1-nano' } },
      speak: { provider: { type: 'deepgram', model: 'aura-2-thalia-en' } },
      listen: {
        provider: {
          type: 'deepgram',
          version: 'v2',
          model: 'flux-general-multi',
          language_hints: ['en', 'es']
        }
      }
    };

    // Resolve the listen configuration so we can detect whether the
    // Deepgram V2 (Flux) listen API is in use. The Flux/V2 listen API
    // rejects the deprecated `agent.language` field, so it must be omitted
    // when the listen provider version is 'v2'.
    const listenConfig = finalDeepgramConfig.listen || {
      provider: {
        type: 'deepgram',
        version: 'v2',
        model: 'flux-general-multi',
        language_hints: ['en', 'es']
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isFluxV2Listen = (listenConfig as any)?.provider?.version === 'v2';

    // Server functions plus optional client-added tools only
    const functions = [
      ...(finalDeepgramConfig.think?.functions || []),
      ...(this.config.additionalFunctions || [])
    ];

    const agentSettings = {
      // `agent.language` is deprecated and rejected by the Deepgram V2
      // (Flux) listen API. Only include it for non-V2 listen providers.
      ...(!isFluxV2Listen && { language: 'en' }),
      listen: listenConfig,
      think: {
        provider: finalDeepgramConfig.think?.provider || { type: 'open_ai', model: 'gpt-4.1-nano' },
        // System prompt is server-controlled and included in the initial
        // Settings payload (previously sent via a follow-up UpdatePrompt).
        prompt: systemPrompt,
        ...(functions.length > 0 && { functions })
      },
      // Apply native Deepgram TTS speed (high quality, server-side) instead
      // of client-side resampling. Range 0.7-1.5; only sent when != 1.0.
      // A pinned languageMode (config) or the resolved active language can
      // override the server's initial voice so the greeting already matches.
      speak: this.applyTtsSpeed(
        this.applyInitialLanguageVoice(finalDeepgramConfig.speak) || {
          provider: { type: 'deepgram', model: 'aura-2-thalia-en' }
        }
      ),
      // Greeting is server-controlled; only set when server provides it
      ...(greeting && { greeting })
    };

    // Our DeepgramAgentConfig types are intentionally loose (server-driven);
    // cast to the SDK's settings shape which they mirror.
    return agentSettings as AgentSettingsObject;
  }

  /**
   * Detect the device's native audio sample rate by probing a default
   * AudioContext (which the browser opens at the hardware rate).
   * Falls back to 48000 if probing fails (e.g. non-browser environment).
   * @returns The native sample rate in Hz (e.g. 48000 or 44100)
   */
  private async detectNativeSampleRate(): Promise<number> {
    try {
      const probe = new AudioContext();
      const rate = probe.sampleRate;
      await probe.close();
      return rate > 0 ? rate : 48000;
    } catch {
      return 48000;
    }
  }

  /**
   * Track consecutive all-zero microphone frames and warn once if the mic
   * appears to be capturing pure silence (~2 seconds of zero PCM). This is
   * the signature of an AudioContext/hardware sample-rate mismatch or an
   * OS-level muted/misrouted input device.
   * @param data - Raw linear16 PCM frame from AgentMicrophone
   */
  private trackMicSilence(data: ArrayBuffer): void {
    if (this.silenceWarned) return;

    const samples = new Int16Array(data);
    let silent = true;
    for (let i = 0; i < samples.length; i++) {
      if (samples[i] !== 0) { silent = false; break; }
    }

    if (!silent) {
      this.silentFrameCount = 0;
      return;
    }

    this.silentFrameCount++;
    // AudioWorklet frames are 128 samples; ~2s of continuous silence
    const threshold = Math.ceil((this.inputSampleRate * 2) / 128);
    if (this.silentFrameCount >= threshold) {
      this.silenceWarned = true;
      console.warn(
        '[GlydeVoice] Microphone is capturing silence (all-zero audio). ' +
        `Input sample rate: ${this.inputSampleRate} Hz. ` +
        'Check OS microphone permissions, the selected input device, and hardware mute.'
      );
    }
  }

  /**
   * Initialize and start the voice session.
   * Guards against double-start; cleans up any prior resources first.
   */
  async start(): Promise<void> {
    if (this.active) {
      console.warn('[GlydeVoice] start() called while already active - ignoring');
      return;
    }
    // Safety cleanup: release any leftover resources from a previous session
    this.cleanup();
    this.active = true;

    try {
      // 1. Fetch config from Unity (system prompt and greeting are server-controlled)
      this.serverConfig = await this.fetchConfig();
      console.log('[GlydeVoice] Fetched config:', this.serverConfig);

      // 2. Authenticate and get ephemeral Deepgram token (send context_type + entity UUIDs when known)
      const { token: deepgramToken, agent_config, deepgram_config: serverDeepgramConfig, language_config: authLanguageConfig } =
        await this.authenticateSession();

      // Resolve the language/voice configuration (auth response > fetched config)
      // and initialise voice-switching state. Older Unity APIs omit the field
      // entirely; the session then stays on its initial voice.
      this.languageConfig = authLanguageConfig || this.serverConfig?.language_config || null;
      this.initLanguageState();

      // Set session context for function calls (client_uuid, job context, etc.)
      this.setSessionContext({
        clientUuid: agent_config?.client_uuid,
        contextId: this.config.contextId,
        contextType: this.config.contextType,
        currentJobUuid: agent_config?.job_uuid
      });

      // System prompt is server-controlled: auth response instructions > fetched config > fallback
      const systemPrompt =
        agent_config?.instructions ||
        this.serverConfig?.system_prompt ||
        'You are not available at the moment. Please try again later. Do not accept any questions.';

      // Get Deepgram config with priority: client override > auth response > server config > defaults
      const deepgramConfig = this.config.deepgramConfig ||
        serverDeepgramConfig ||
        this.serverConfig?.deepgram_config;

      const greeting = agent_config?.greeting ?? deepgramConfig?.greeting;
      const agentSettings = this.buildAgentSettings(deepgramConfig, systemPrompt, greeting);

      // 3. Initialize audio (player + pre-acquired microphone) before connecting
      this.player = new AgentPlayer({ sampleRate: this.outputSampleRate });

      // Capture at the device's native sample rate. AgentMicrophone forces
      // its AudioContext to the rate we pass; if that differs from the
      // hardware rate (e.g. 48000 requested on a 44.1 kHz input), Chrome's
      // MediaStreamAudioSourceNode can deliver silence (all-zero PCM). The
      // resolved rate is declared to Deepgram in the session audio settings.
      this.inputSampleRate = await this.detectNativeSampleRate();
      console.log('[GlydeVoice] Microphone input sample rate:', this.inputSampleRate);
      this.silentFrameCount = 0;
      this.silenceWarned = false;

      this.microphone = new AgentMicrophone(
        (data: ArrayBuffer) => {
          // Forward frames only while the session is live and unmuted.
          // AgentSession additionally buffers frames until SettingsApplied.
          if (!this.active || !this.micLive || this.isMuted || !this.session) {
            return;
          }
          this.trackMicSilence(data);
          this.session.sendAudio(data);
        },
        {
          sampleRate: this.inputSampleRate,
          echoCancellation: true,
          noiseSuppression: true
        }
      );

      // Pre-acquire the microphone before the WebSocket connects so the
      // stream is ready the instant settings are applied. Frames are gated
      // by `micLive` until then, so nothing is transmitted early.
      await this.microphone.start();

      // 4. Create the Deepgram Voice Agent session.
      // Reconnect is disabled to preserve the SDK's long-standing behavior:
      // an unexpected disconnect surfaces as a 'close' event for the host app.
      this.session = new AgentSession({
        auth: { tokenFactory: async () => deepgramToken },
        agent: agentSettings,
        audio: {
          input: { encoding: 'linear16', sampleRate: this.inputSampleRate },
          output: { encoding: 'linear16', sampleRate: this.outputSampleRate }
        },
        reconnect: { enabled: false },
        ...(deepgramConfig?.tags && deepgramConfig.tags.length > 0 && { tags: deepgramConfig.tags })
      });

      this.bindSessionEvents(this.session, agent_config);

      // 5. Connect (AgentSession sends Settings automatically on open)
      await this.session.connect();

      // Render UI if container is specified
      this.renderUI();

    } catch (error) {
      console.error('[GlydeVoice] Error starting session:', error);
      this.active = false;
      this.emitEvent({ type: 'error', payload: error });
      throw error;
    }
  }

  /**
   * Wire AgentSession events to GlydeVoice events (public event contract
   * unchanged: open/close/error/ready/user_speaking/agent_speaking/
   * microphone_ready/transcript/context_switch).
   *
   * @param session - The AgentSession to bind
   * @param agentConfig - Server agent config (included in the 'open' event payload)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private bindSessionEvents(session: AgentSession, agentConfig: any): void {
    let closeEmitted = false;

    session.on('connected', () => {
      this.emitEvent({ type: 'open', payload: { config: agentConfig, serverConfig: this.serverConfig } });
    });

    session.on('welcome', () => {
      this.emitEvent({ type: 'ready' });
    });

    session.on('settings-applied', () => {
      // Agent is fully configured (prompt included in Settings) - open the mic gate
      this.micLive = true;
      this.emitEvent({ type: 'microphone_ready' });
    });

    session.on('user-started-speaking', () => {
      this.emitEvent({ type: 'user_speaking', payload: true });
      // Barge-in: flush queued agent audio immediately
      this.player?.interrupt();
      this.stopPlaybackDrainWatch();
      if (this.isAgentSpeaking) {
        this.isAgentSpeaking = false;
        this.emitEvent({ type: 'agent_speaking', payload: false });
      }
      this.contextSwitchManager.flushPendingContextSwitch();
    });

    session.on('agent-thinking', () => {
      // Deepgram's current agent API has no UserStoppedSpeaking message;
      // AgentThinking marks end-of-turn, so reset the user_speaking flag here.
      this.emitEvent({ type: 'user_speaking', payload: false });
    });

    session.on('conversation-text', (msg) => {
      // Flux Multilingual reports detected languages per user turn; run the
      // voice switch before the reply is synthesised so the agent's answer to
      // a language change already uses the new voice.
      if (msg.role === 'user') {
        this.handleLanguageDetection(msg);
      }
      if (msg.content && msg.content.trim()) {
        // Normalize role: Deepgram sends 'assistant', we use 'agent'
        const normalizedRole = msg.role === 'assistant' ? 'agent' : 'user';
        if (this.config.onTranscript) {
          this.config.onTranscript(msg.content, normalizedRole as 'user' | 'agent');
        }
        this.emitEvent({ type: 'transcript', payload: { text: msg.content, role: normalizedRole } });
        this.saveTranscript(msg.content, msg.role);
      }
    });

    session.on('agent-started-speaking', () => {
      this.markAgentSpeaking();
    });

    session.on('audio', (chunk) => {
      this.player?.queue(chunk);
      // Mark agent as speaking when audio arrives even if
      // AgentStartedSpeaking has not been received yet
      this.markAgentSpeaking();
    });

    session.on('agent-audio-done', () => {
      // Agent finished sending audio; watch the playback queue drain so we
      // can emit agent_speaking=false when audio actually stops playing.
      this.startPlaybackDrainWatch();
    });

    session.on('function-call-request', (msg) => {
      this.handleFunctionCallRequest(msg);
    });

    session.on('error', (msg) => {
      console.error('[GlydeVoice] Agent error:', msg);
      this.emitEvent({ type: 'error', payload: msg });
    });

    session.on('sdk-error', (err) => {
      console.error('[GlydeVoice] Session error:', err);
      this.emitEvent({ type: 'error', payload: err });
    });

    session.on('disconnected', () => {
      this.cleanup();
      if (!closeEmitted) {
        closeEmitted = true;
        this.emitEvent({ type: 'close' });
      }
    });
  }

  /**
   * Mark the agent as speaking and emit agent_speaking=true once per turn.
   */
  private markAgentSpeaking(): void {
    if (!this.isAgentSpeaking) {
      this.isAgentSpeaking = true;
      this.emitEvent({ type: 'agent_speaking', payload: true });
    }
  }

  /**
   * Poll the player until queued agent audio has finished, then emit
   * agent_speaking=false and flush any pending context switch (context
   * switches are deferred until the agent has stopped speaking).
   */
  private startPlaybackDrainWatch(): void {
    this.stopPlaybackDrainWatch();
    this.playbackDrainTimer = setInterval(() => {
      const remaining = this.player?.getRemainingPlaybackTime() ?? 0;
      if (remaining <= 0) {
        this.stopPlaybackDrainWatch();
        if (this.isAgentSpeaking) {
          this.isAgentSpeaking = false;
          this.emitEvent({ type: 'agent_speaking', payload: false });
        }
        this.contextSwitchManager.flushPendingContextSwitch();
      }
    }, 200);
  }

  /**
   * Stop the playback-drain poll timer if running.
   */
  private stopPlaybackDrainWatch(): void {
    if (this.playbackDrainTimer) {
      clearInterval(this.playbackDrainTimer);
      this.playbackDrainTimer = null;
    }
  }

  /**
   * Apply the configured native Deepgram TTS speed to a speak configuration.
   * Only injects `speed` for Deepgram providers and only when it differs from
   * 1.0 (default), leaving other providers untouched.
   * @param speak - Speak configuration from the resolved Deepgram config
   * @returns Speak configuration with `provider.speed` applied when applicable
   * @see https://developers.deepgram.com/docs/tts-voice-controls
   */
  private applyTtsSpeed(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    speak: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any {
    if (!speak || this.playbackSpeed === 1.0) return speak;

    const provider = speak.provider;
    if (provider && (provider.type === 'deepgram' || provider.type === undefined)) {
      return {
        ...speak,
        provider: { ...provider, speed: this.playbackSpeed }
      };
    }

    return speak;
  }

  /**
   * Apply the resolved active language's voice to the initial speak config.
   *
   * Runs after `initLanguageState()` so a config-pinned `languageMode`
   * ('es') makes the greeting itself Spanish-native. Returns the config
   * unchanged when there is no language map or the active language already
   * matches (the default English-first flow).
   *
   * @param speak - Speak configuration from the resolved Deepgram config
   * @returns Speak configuration with the active language's voice model
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private applyInitialLanguageVoice(speak: any): any {
    if (!speak) return speak;
    const voice = this.activeLanguage ? this.languageConfig?.tts_voices?.[this.activeLanguage] : undefined;
    if (!voice) return speak;

    const provider = speak.provider;
    if (!provider || (provider.type !== 'deepgram' && provider.type !== undefined)) return speak;

    return {
      ...speak,
      provider: { ...provider, model: voice }
    };
  }

  /**
   * Save transcript to Unity backend
   */
  private async saveTranscript(content: string, role: string): Promise<void> {
    if (!this.config.contextId || !content) return;

    try {
      await fetch(`${this.unityUrl}/api/unity/voice/transcript`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          context_id: this.config.contextId,
          content,
          role: role === 'assistant' ? 'assistant' : 'user'
        })
      });
    } catch {
      // Silently fail - transcript saving is not critical
    }
  }

  /**
   * Initialise language-switching state from the resolved server config.
   *
   * Rules:
   * - No language_config (older Unity API) or a single-voice map → auto
   *   switching disabled; the session keeps its initial voice.
   * - voice_switching 'off' (tenant pinned one voice) → auto switching
   *   disabled, but manual setLanguage() still swaps voices from the map.
   * - A languageMode override from config pins the initial language.
   */
  private initLanguageState(): void {
    const cfg = this.languageConfig;
    if (!cfg || !cfg.tts_voices || Object.keys(cfg.tts_voices).length === 0) {
      this.autoSwitchEnabled = false;
      this.activeLanguage = null;
      return;
    }

    const override = this.config.languageMode;
    if (override && override !== 'auto' && cfg.tts_voices[override]) {
      this.activeLanguage = override;
      this.autoSwitchEnabled = false;
    } else {
      this.activeLanguage = cfg.languages[0] ?? Object.keys(cfg.tts_voices)[0] ?? null;
      this.autoSwitchEnabled = cfg.voice_switching === 'auto';
    }
  }

  /**
   * Normalise a BCP-47 language code to the tenant-configured language key
   * (e.g. 'es-419' → 'es'; 'en-US' → 'en').
   * @param language - Raw language code from detection or user input
   * @returns Tenant language key, or null when not configured
   */
  private normalizeLanguage(language: string): string | null {
    const cfg = this.languageConfig;
    if (!cfg) return null;
    if (cfg.tts_voices[language]) return language;
    const base = language.split('-')[0].toLowerCase();
    return cfg.tts_voices[base] ? base : null;
  }

  /**
   * Extract the detected language(s) from a Deepgram ConversationText message.
   *
   * Flux Multilingual attaches `languages[]` per user turn (server-driven JSON).
   * The bundled `@deepgram/sdk` version does not type the field, so it is read
   * via a narrow cast rather than upgrading the dependency.
   *
   * @param msg - ConversationText message from the session
   * @returns First detected language code, or null when absent
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extractDetectedLanguage(msg: any): string | null {
    const languages: unknown = (msg as { languages?: unknown })?.languages;
    if (Array.isArray(languages) && languages.length > 0 && typeof languages[0] === 'string') {
      return languages[0];
    }
    return null;
  }

  /**
   * Handle a user-turn ConversationText for voice switching: when auto mode is
   * active and the detected language differs from the active one, swap the TTS
   * voice via Deepgram UpdateSpeak and emit language_changed.
   *
   * Called from the conversation-text handler below before the transcript is
   * emitted, so the very reply to the language change already uses the new voice.
   *
   * @param msg - ConversationText message from the session
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleLanguageDetection(msg: any): void {
    if (!this.autoSwitchEnabled) return;

    const detected = this.extractDetectedLanguage(msg);
    if (!detected) return;

    const normalized = this.normalizeLanguage(detected);
    if (!normalized || normalized === this.activeLanguage) return;

    this.applyLanguage(normalized, 'detection');
  }

  /**
   * Apply a language switch: update state and send Deepgram UpdateSpeak with
   * the language's Aura-2 voice. No-op when the language is not configured or
   * the session is not connected (the state still tracks the request so the
   * next reconnect uses the right voice).
   *
   * @param language - Tenant-configured language key (e.g. 'en', 'es')
   * @param source - What triggered the switch ('detection' | 'manual')
   */
  private applyLanguage(language: string, source: 'detection' | 'manual'): void {
    const voice = this.languageConfig?.tts_voices?.[language];
    if (!voice) {
      console.warn(`[GlydeVoice] Language '${language}' has no configured voice; ignoring switch`);
      return;
    }

    const changed = this.activeLanguage !== language;
    this.activeLanguage = language;

    if (this.session && this.session.state === 'connected') {
      this.session.updateSpeak(this.applyTtsSpeed({
        provider: { type: 'deepgram', model: voice }
      }));
    }

    if (changed) {
      this.emitEvent({
        type: 'language_changed',
        payload: { language, source, voice } satisfies LanguageChangedPayload
      });
    }
  }

  /**
   * Manually set the agent's spoken language (widget toggle).
   *
   * Passing 'auto' (re-)enables detection-driven switching; passing a language
   * code pins the voice to that language for the remainder of the session
   * (until 'auto' is selected again).
   *
   * @param mode - 'auto' or a tenant-configured language key ('en', 'es', ...)
   */
  setLanguage(mode: 'auto' | string): void {
    const cfg = this.languageConfig;
    if (!cfg) {
      console.warn('[GlydeVoice] setLanguage ignored: no language_config from server (older Unity API?)');
      return;
    }

    if (mode === 'auto') {
      this.autoSwitchEnabled = cfg.voice_switching === 'auto';
      // Re-align to the first configured language when returning to auto
      const first = cfg.languages[0] ?? Object.keys(cfg.tts_voices)[0];
      if (first) this.applyLanguage(first, 'manual');
      return;
    }

    const normalized = this.normalizeLanguage(mode);
    if (!normalized) {
      console.warn(`[GlydeVoice] setLanguage('${mode}') ignored: language not in tenant map`, cfg.tts_voices);
      return;
    }

    this.autoSwitchEnabled = false;
    this.applyLanguage(normalized, 'manual');
  }

  /**
   * Get the language currently spoken by the agent voice, or null when no
   * language_config was provided by the server.
   */
  getActiveLanguage(): string | null {
    return this.activeLanguage;
  }

  /**
   * Get the resolved language configuration from the server (voice map and
   * switching policy), or null on older Unity APIs.
   */
  getLanguageConfig(): VoiceLanguageConfig | null {
    return this.languageConfig;
  }

  /**
   * Toggle mute state
   * @param muted - Whether to mute the microphone
   */
  setMuted(muted: boolean): void {
    this.isMuted = muted;
  }

  /**
   * Get current mute state
   */
  getMuted(): boolean {
    return this.isMuted;
  }

  /**
   * Check if the voice agent is currently active
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * Get the current server configuration
   */
  getServerConfig(): VoiceConfig | null {
    return this.serverConfig;
  }

  /**
   * Stop the voice session
   */
  stop(): void {
    this.active = false;
    this.contextSwitchManager.cancelPendingSwitch();
    this.cleanup();
  }

  /**
   * Cleanup resources (microphone, player, session, timers). Idempotent.
   */
  private cleanup(): void {
    this.micLive = false;
    this.stopPlaybackDrainWatch();

    // Reset language-switching state; the next start() re-resolves it from
    // the fresh auth/config response.
    this.languageConfig = null;
    this.activeLanguage = null;
    this.autoSwitchEnabled = false;

    if (this.microphone) {
      this.microphone.stop();
      this.microphone = null;
    }

    if (this.player) {
      this.player.dispose();
      this.player = null;
    }

    if (this.session) {
      const session = this.session;
      // Null the field first: disconnect() triggers the 'disconnected'
      // listener, which calls cleanup() again (no-op) and emits 'close'.
      this.session = null;
      session.disconnect();
    }
  }

  /**
   * Emit event to the configured callback (implements BaseGlydeClient abstract method)
   */
  protected emitEvent(event: VoiceEvent): void {
    if (this.config.onEvent) {
      this.config.onEvent(event);
    }
  }

  /**
   * Render a simple UI widget (optional)
   */
  private renderUI(): void {
    if (!this.config.container) return;

    const container = typeof this.config.container === 'string'
      ? document.querySelector(this.config.container)
      : this.config.container;

    if (container) {
      container.innerHTML = `
        <div style="padding: 20px; border: 1px solid #ccc; border-radius: 8px; background: #fff;">
          <h3>Glyde Voice Agent</h3>
          <p>Status: Active</p>
          <p>Context: ${this.config.contextType}</p>
          <button onclick="this.closest('div').remove()">Close</button>
        </div>
      `;
    }
  }

  /**
   * Handle a function call request from Deepgram Voice Agent
   * Routes function execution through the Unity voice function endpoint for proper authentication
   *
   * Deepgram sends an array of functions in each request, so we process each one
   * and send individual responses back via the session.
   *
   * @param request - The function call request from Deepgram containing functions array
   * @see https://developers.deepgram.com/docs/voice-agents-function-calling
   */
  private async handleFunctionCallRequest(request: FunctionCallRequestMessage): Promise<void> {
    // Process each function call in the request
    for (const func of request.functions) {
      console.log('[GlydeVoice] Function call request:', func.name, func.arguments);

      // Parse arguments from JSON string
      let input: Record<string, unknown> = {};
      try {
        input = func.arguments ? JSON.parse(func.arguments) : {};
      } catch (parseError) {
        console.warn('[GlydeVoice] Failed to parse function arguments:', parseError);
      }

      let output: string;

      try {
        // Handle end_conversation locally (no server call needed)
        if (func.name === 'end_conversation') {
          output = await this.handleEndConversation(input);
        } else {
          // Route other functions through Unity voice function endpoint
          // This ensures proper authentication and data controls
          output = await this.executeVoiceFunction(func.name, func.id, input);
        }
      } catch (error) {
        console.error(`[GlydeVoice] Function call error:`, error);
        output = JSON.stringify({
          error: 'Function execution failed',
          details: error instanceof Error ? error.message : String(error)
        });
      }

      // Send the function call response back to Deepgram
      if (this.session && this.session.state === 'connected') {
        this.session.sendFunctionCallResponse(func.id, func.name, output);
        console.log('[GlydeVoice] Function response sent:', func.name);
      } else {
        console.error('[GlydeVoice] Cannot send function response - session not connected');
      }

      // Delegate context_switch detection to ContextSwitchManager (emits event via onContextSwitchRequested callback)
      this.contextSwitchManager.processContextSwitchFromOutput(output);
    }
  }

  /**
   * Execute a voice function through the Unity API with proper authentication
   * Uses the dedicated /api/unity/voice/function endpoint which handles
   * publishable key authentication and data controls
   *
   * @param functionName - Name of the function to execute
   * @param functionCallId - Unique ID for tracking
   * @param input - Function input parameters
   * @returns JSON string with function result
   */
  private async executeVoiceFunction(
    functionName: string,
    functionCallId: string,
    input: Record<string, unknown>
  ): Promise<string> {
    console.log('[GlydeVoice] Executing voice function via Unity API:', functionName);

    try {
      const response = await fetch(`${this.unityUrl}/api/unity/voice/function`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          function_name: functionName,
          function_call_id: functionCallId,
          input,
          context: {
            context_id: this.sessionContext.contextId,
            context_type: this.sessionContext.contextType,
            current_job_uuid: this.sessionContext.currentJobUuid,
            candidate_uuid: this.sessionContext.contextType === 'candidate_discover' ? this.sessionContext.contextId : undefined
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.error?.message || errorData.message || `HTTP ${response.status}`;
        console.error('[GlydeVoice] Voice function API call failed:', response.status, message);
        throw new Error(message);
      }

      const result = await response.json();

      if (result.success && result.data?.output !== undefined) {
        const output = result.data.output;
        // Log when backend returned an operational failure (e.g. job search error)
        if (typeof output === 'object' && output !== null && (output as Record<string, unknown>).success === false) {
          const errMsg = (output as Record<string, unknown>).error ?? (output as Record<string, unknown>).fallback_message;
          console.warn('[GlydeVoice] Voice function backend reported failure:', functionName, errMsg);
        }
        return typeof output === 'string' ? output : JSON.stringify(output);
      }

      console.error('[GlydeVoice] Voice function invalid response:', result);
      throw new Error('Invalid response from voice function endpoint');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Function execution failed';
      console.error('[GlydeVoice] Voice function error:', message, error);
      return JSON.stringify({
        success: false,
        error: message,
        fallback_message: 'I apologize, but I\'m having trouble with that request right now. Is there something else I can help you with?'
      });
    }
  }

  /**
   * Handle the end_conversation function locally
   * Gracefully ends the voice session (no server call needed)
   *
   * @param input - Function parameters including the trigger phrase
   * @returns Acknowledgment string for the agent
   */
  private async handleEndConversation(input: Record<string, unknown>): Promise<string> {
    const trigger = (input.reason || input.item) as string || 'user request';
    console.log(`[GlydeVoice] End conversation triggered by: ${trigger}`);

    // Schedule the stop after a brief delay to allow goodbye message
    setTimeout(() => {
      this.stop();
    }, 2000);

    return JSON.stringify({
      success: true,
      message: 'Conversation ending. Say goodbye to the user.',
      trigger_phrase: trigger
    });
  }

  /**
   * Set session context for function calls
   * Called internally after authentication to provide job context for function routing
   * Context is passed to the backend voice function endpoint for proper data controls
   *
   * @param context - Session context with contextId, contextType, and job details
   */
  public setSessionContext(context: {
    clientUuid?: string;
    contextId?: string;
    contextType?: VoiceContextType;
    currentJobUuid?: string;
    candidateUuid?: string;
  }): void {
    this.sessionContext = { ...this.sessionContext, ...context };
    console.log('[GlydeVoice] Session context updated:', {
      hasContextId: !!context.contextId,
      contextType: context.contextType,
      hasCandidateUuid: !!this.sessionContext.candidateUuid,
      hasJobUuid: !!context.currentJobUuid
    });
  }

  /**
   * Update context and system prompt in-place over the existing session (no reconnect).
   * Fetches the new config from Unity, sends Deepgram UpdatePrompt so the agent uses the new
   * instructions, and updates local config/sessionContext. Function calls will then send the
   * new context to the backend. Smoother UX than full reinitialize.
   *
   * @param contextType - New voice context type (e.g. 'screening', 'candidate_discover')
   * @param contextId - New context ID (e.g. application_uuid, candidate_uuid)
   * @throws If the session is not connected or config fetch fails
   */
  public async updateContextAndPrompt(contextType: VoiceContextType, contextId: string): Promise<void> {
    if (!this.session || this.session.state !== 'connected') {
      throw new Error('Cannot update context: session not connected');
    }

    console.log('[GlydeVoice] Fetching new config for context switch:', contextType, contextId);
    const newConfig = await this.fetchConfigForContext(contextType, contextId);
    console.log('[GlydeVoice] Received new config for context switch:', {
      contextType,
      contextId,
      hasSystemPrompt: !!newConfig?.system_prompt,
      promptLength: newConfig?.system_prompt?.length || 0,
      promptPreview: newConfig?.system_prompt?.substring(0, 200) || '(no prompt)'
    });

    const newPrompt = newConfig?.system_prompt || 'You are a helpful AI assistant.';

    // Build a strong context switch instruction that tells Deepgram to completely change behavior
    // We need to be very explicit because UpdatePrompt appends rather than replaces
    const contextDescription = this.getContextDescription(contextType);
    const previousContext = this.getContextDescription(this.config.contextType || 'discovery');

    const replacementPrompt = `
---
## CRITICAL CONTEXT SWITCH - IMMEDIATE EFFECT
---

**STOP**: The previous conversation context ("${previousContext}") has ended.

**NEW CONTEXT**: You are now in "${contextDescription}" mode.

**IMPORTANT**:
- Do NOT continue any screening agenda, interview questions, or job-specific requirements from before.
- Do NOT reference or follow any previous "Requirements", "Responsibilities", or "Screening Agenda" sections.
- Your role has changed - follow ONLY the new instructions below.
- Do NOT greet, welcome, or re-introduce yourself. This is a continuation of the same call. Transition naturally without any greeting.

---
## NEW INSTRUCTIONS (replace all previous)
---

${newPrompt}`;
 // TODO ±±ONS6MISM±± #VoiceContextPrompts The deepgram update config APPENDS to the existin system prompt, so if a user switches context a few times we will have a confusing set of instructions being sent. We need to monitor prompt length and do a re-init (somehow more smoothlly for the user, or with a warning at least) @RobertoRGlyde
    console.log('[GlydeVoice] Sending UpdatePrompt to Deepgram:', {
      promptLength: replacementPrompt.length,
      previousContext,
      newContext: contextDescription,
      headerPreview: replacementPrompt.substring(0, 500)
    });
    this.session.updatePrompt(replacementPrompt);

    this.config = { ...this.config, contextType, contextId };
    this.setSessionContext({ contextType, contextId });
    this.serverConfig = newConfig;

    // Re-resolve language config from the new context's config (keeps voice
    // switching consistent across a context switch mid-session).
    this.languageConfig = newConfig?.language_config || this.languageConfig;
    this.initLanguageState();

    console.log('[GlydeVoice] Context and prompt updated in-place:', contextType, contextId);
  }

  /**
   * Get a human-readable description of a context type for use in context switch messages.
   * @param contextType - The voice context type
   * @returns Human-readable description of the context
   */
  private getContextDescription(contextType: VoiceContextType | undefined): string {
    switch (contextType) {
      case 'screening':
        return 'job screening interview';
      case 'candidate_discover':
        return 'job discovery and exploration';
      case 'job_apply':
        return 'job application submission';
      case 'discovery':
        return 'general job discovery';
      case 'recruiter':
        return 'recruiter assistant';
      case 'custom':
        return 'custom conversation';
      default:
        return 'general assistant';
    }
  }

  /**
   * Reinitialize the voice session with a new context (e.g. after context_switch from the server).
   * Stops the current session, updates config and session context, then starts a new session
   * so chat history is stored under the correct application/placeholder.
   * Use updateContextAndPrompt for a smoother UX when the session is already connected.
   *
   * @param contextType - New voice context type (e.g. 'screening', 'candidate_discover')
   * @param contextId - New context ID (e.g. application_uuid, candidate_uuid)
   */
  public async reinitializeWithContext(contextType: VoiceContextType, contextId: string): Promise<void> {
    this.config = { ...this.config, contextType, contextId };
    this.setSessionContext({ contextType, contextId });
    this.stop();
    await new Promise((r) => setTimeout(r, 800));
    await this.start();
  }
}
